/**
 * 「分拣机」子页，移植产品原型 components/SorterPanel.tsx：交叉带按所选区间分时段、DWS 按设备分列，次级指标默认折叠。
 * 指标口径和数值来自「分拣机运营监控」页同一张表（ads_sorting_sorter_operation_statistics_df）。
 *
 * 设备名称来自分拣机管理表：
 *  - 交叉带：只有 1 台就直接用设备名称做表标题，不加「整体」；有多台则先给一张「整体」表（各台合计），再按设备名称各给一张表。
 *  - DWS：只有 1 台就只有那一列，不加「整体」；有多台则第一列是「整体」，后面按设备名称分列。
 * 该仓没有对应设备 / 数据时，表格结构保留、所有数值留空（—）。
 *
 * 与原型的差别：字段按 types.ts 的 SorterHourRow（hourKey 直接给出，不再拼 stat_date + stat_hour）；
 * 「整体」/ 占位表用标记位区分而不是比对标题文字（标题要翻译）；文案走 i18n。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SorterDevice, SorterHourRow } from '../types';
import { useFmt } from '../lib/useFmt';
import { I18N } from '../lib/useLabels';
import HoverCard from './HoverCard';

const C = {
    border: '#E5E6EB',
    borderLight: '#F2F3F5',
    text1: '#1D2129',
    text2: '#4E5969',
    text3: '#86909C',
    headBg: '#F7F8FA',
    primary: '#722ED1'
};
const n = (v: number | null) => v ?? 0;
/** 交叉带设计产能 包次/小时 */
const DESIGN_CAPACITY = 25000;

interface Bucket {
    scan: number;
    dur: number;
    recog: number;
    distinct: number;
    dropDistinct: number;
    dropExpect: number;
}
const emptyBucket = (): Bucket => ({ scan: 0, dur: 0, recog: 0, distinct: 0, dropDistinct: 0, dropExpect: 0 });
const addBucket = (a: Bucket, b: Bucket) => {
    a.scan += b.scan;
    a.dur += b.dur;
    a.recog += b.recog;
    a.distinct += b.distinct;
    a.dropDistinct += b.dropDistinct;
    a.dropExpect += b.dropExpect;
};

interface DwsBucket {
    scan: number;
    dur: number;
}

interface Fmts {
    n: (v: number) => string;
    h: (v: number) => string;
    rate: (num: number, den: number) => string;
    pct: (a: number, b: number) => string;
}

/** 指标行。名称 / 说明见 i18n sorter.cbsRows.<key>.label / .info；info=false 的行没有说明 */
interface Metric<B> {
    key: string;
    level: number;
    info: boolean;
    get?: (b: B, f: Fmts) => string;
    hot?: (b: B) => boolean;
}
export const CBS_ROWS: Metric<Bucket>[] = [
    { key: 'dur', level: 0, info: true, get: (b, f) => f.h(b.dur) },
    { key: 'stopN', level: 1, info: true },
    { key: 'stopH', level: 1, info: true },
    { key: 'scan', level: 0, info: true, get: (b, f) => f.n(b.scan) },
    {
        key: 'perHour',
        level: 1,
        info: true,
        get: (b, f) => f.rate(b.scan, b.dur),
        hot: (b) => b.dur > 0 && b.scan / b.dur < DESIGN_CAPACITY
    },
    { key: 'feed', level: 2, info: true },
    { key: 'rec', level: 0, info: true, get: (b, f) => f.pct(b.distinct, b.scan) },
    { key: 'bar', level: 1, info: true, get: (b, f) => f.pct(b.recog, b.scan) },
    { key: 'res', level: 1, info: true, get: (b, f) => f.pct(b.distinct, b.recog) },
    { key: 'distinct', level: 0, info: false, get: (b, f) => f.n(b.distinct) },
    {
        key: 'valid',
        level: 1,
        info: true,
        get: (b, f) => (b.dropDistinct === 0 ? '—' : `${f.n(b.dropDistinct)} · ${f.pct(b.dropDistinct, b.distinct)}`)
    },
    { key: 'acc', level: 2, info: true, get: (b, f) => f.pct(b.dropExpect, b.dropDistinct) },
    { key: 'abn', level: 1, info: true },
    { key: 'abnChute', level: 0, info: true },
    { key: 'loop', level: 0, info: true },
    { key: 'loopChute', level: 1, info: true }
];
export const DWS_ROWS: Metric<DwsBucket>[] = [
    { key: 'scan', level: 0, info: true, get: (b, f) => f.n(b.scan) },
    { key: 'dur', level: 1, info: true, get: (b, f) => f.h(b.dur) },
    { key: 'perHour', level: 1, info: true, get: (b, f) => f.rate(b.scan, b.dur) },
    { key: 'normal', level: 1, info: true },
    { key: 'abn', level: 1, info: true },
    { key: 'over', level: 0, info: true },
    { key: 'weight', level: 0, info: true }
];

const Info: React.FC<{ text?: string }> = ({ text }) => {
    if (!text) return null;
    return (
        <HoverCard card={text} accent={C.text3} style={{ display: 'inline-flex', marginLeft: 4 }}>
            <span
                style={{
                    width: 12,
                    height: 12,
                    border: '1px solid #C9CDD4',
                    borderRadius: '50%',
                    color: C.text3,
                    fontSize: 9,
                    lineHeight: '10px',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    cursor: 'help',
                    display: 'inline-block'
                }}
            >
                i
            </span>
        </HoverCard>
    );
};

/** 把层级行折叠成树：某行只有在它的所有祖先都展开时才显示 */
function useVisible<T extends { key: string; level: number }>(rows: T[], open: Set<string>) {
    return React.useMemo(() => {
        const out: { row: T; hasKids: boolean }[] = [];
        const stack: string[] = [];
        rows.forEach((r, i) => {
            stack.length = r.level;
            const hasKids = !!rows[i + 1] && rows[i + 1].level > r.level;
            const visible = stack.every((k) => open.has(k));
            if (visible) out.push({ row: r, hasKids });
            stack[r.level] = r.key;
        });
        return out;
    }, [rows, open]);
}

interface Dev {
    code: string;
    name: string;
}

const isCbs = (t: number) => t === 2 || t === 5;

interface Props {
    rows: SorterHourRow[];
    devices: SorterDevice[];
    /** 所选区间内的 hub 当地整点 'YYYY-MM-DD HH' */
    labels: string[];
    hub: string;
}

const SorterPanel: React.FC<Props> = ({ rows, devices, labels, hub }) => {
    const { t } = useTranslation();
    const fmt = useFmt();
    const k = (key: string, opt: Record<string, string | number> = {}): string => t(`${I18N}.sorter.${key}`, opt) ?? '';
    const [openC, setOpenC] = React.useState<Set<string>>(new Set());
    const [openD, setOpenD] = React.useState<Set<string>>(new Set());
    const toggle = (set: Set<string>, setter: (s: Set<string>) => void, key: string) => {
        const nx = new Set(set);
        if (nx.has(key)) nx.delete(key);
        else nx.add(key);
        setter(nx);
    };

    const f: Fmts = React.useMemo(
        () => ({
            n: (v) => (v === 0 ? '—' : fmt.num(Math.round(v))),
            h: (v) => (v === 0 ? '—' : `${v.toFixed(1)}h`),
            rate: (num, den) => (den === 0 ? '—' : `${fmt.num(Math.round(num / den))}/h`),
            pct: (a, b) => (b === 0 ? '—' : `${((a / b) * 100).toFixed(1)}%`)
        }),
        [fmt]
    );

    const mine = React.useMemo(() => rows.filter((r) => r.hub === hub), [rows, hub]);
    const inRange = React.useMemo(() => new Set(labels), [labels]);

    // 设备清单：管理表里配置的 + 数据里出现过但管理表没有的（名称退回编码）
    const devList = React.useMemo(() => {
        const build = (cbs: boolean): Dev[] => {
            const m = new Map<string, Dev>();
            for (const d of devices) {
                if (d.hub === hub && (cbs ? isCbs(d.sorterType) : d.sorterType === 1))
                    m.set(d.sorterCode, { code: d.sorterCode, name: d.sorterName || d.sorterCode });
            }
            for (const r of mine) {
                if ((cbs ? isCbs(r.sorterType) : r.sorterType === 1) && !m.has(r.sorterCode))
                    m.set(r.sorterCode, { code: r.sorterCode, name: r.sorterCode });
            }
            return [...m.values()].sort((a, b) => a.name.localeCompare(b.name, 'zh-CN', { numeric: true }));
        };
        return { cbs: build(true), dws: build(false) };
    }, [devices, mine, hub]);

    // 交叉带：设备 × 小时
    const cbsData = React.useMemo(() => {
        const byDev = new Map<string, Map<string, Bucket>>();
        for (const r of mine) {
            if (!isCbs(r.sorterType) || !inRange.has(r.hourKey)) continue;
            const hours = byDev.get(r.sorterCode) ?? new Map<string, Bucket>();
            const b = hours.get(r.hourKey) ?? emptyBucket();
            addBucket(b, {
                scan: n(r.crossBeltScanCount),
                dur: n(r.crossBeltDurationHours),
                recog: n(r.crossBeltRecognizedTrackingCount),
                distinct: n(r.crossBeltDistinctTrackingCount),
                dropDistinct: n(r.dropDistinctTrackingCount),
                dropExpect: n(r.dropInExpectChuteDistinctCount)
            });
            hours.set(r.hourKey, b);
            byDev.set(r.sorterCode, hours);
        }
        // 每个表块：类型 + 设备 + 每小时数据 + 汇总
        type Kind = 'overall' | 'device' | 'placeholder';
        const mkBlock = (kind: Kind, dev: Dev | null, hoursList: Map<string, Bucket>[]) => {
            const byHour = new Map<string, Bucket>();
            const total = emptyBucket();
            for (const h of hoursList) {
                h.forEach((b, key) => {
                    const sum = byHour.get(key) ?? emptyBucket();
                    addBucket(sum, b);
                    byHour.set(key, sum);
                    addBucket(total, b);
                });
            }
            return { key: dev ? `d|${dev.code}` : kind, kind, dev, byHour, total };
        };
        const devs = devList.cbs;
        const blocks: ReturnType<typeof mkBlock>[] = [];
        if (devs.length > 1)
            blocks.push(
                mkBlock(
                    'overall',
                    null,
                    devs.map((d) => byDev.get(d.code) ?? new Map())
                )
            );
        for (const d of devs) blocks.push(mkBlock('device', d, [byDev.get(d.code) ?? new Map()]));
        if (devs.length === 0) blocks.push(mkBlock('placeholder', null, []));
        return blocks;
    }, [mine, inRange, devList.cbs]);

    // DWS：设备 × 区间汇总
    const dwsCols = React.useMemo(() => {
        const per = new Map<string, DwsBucket>();
        for (const r of mine) {
            if (r.sorterType !== 1 || !inRange.has(r.hourKey)) continue;
            const b = per.get(r.sorterCode) ?? { scan: 0, dur: 0 };
            b.scan += n(r.dwsScanCount);
            b.dur += n(r.dwsDurationHours);
            per.set(r.sorterCode, b);
        }
        const devs = devList.dws;
        // 「整体」列的名称在渲染时翻译
        const cols: { key: string; name: string; overall: boolean; b: DwsBucket }[] = devs.map((d) => ({
            key: `d|${d.code}`,
            name: d.name,
            overall: false,
            b: per.get(d.code) ?? { scan: 0, dur: 0 }
        }));
        if (devs.length > 1) {
            const scan = cols.reduce((a, c) => a + c.b.scan, 0);
            const wsum = cols.reduce((a, c) => a + c.b.scan * c.b.dur, 0);
            cols.unshift({ key: 'overall', name: '', overall: true, b: { scan, dur: scan > 0 ? wsum / scan : 0 } });
        }
        if (devs.length === 0) cols.push({ key: 'placeholder', name: 'DWS', overall: false, b: { scan: 0, dur: 0 } });
        return cols;
    }, [mine, inRange, devList.dws]);

    const visC = useVisible(CBS_ROWS, openC);
    const visD = useVisible(DWS_ROWS, openD);
    const hasData = mine.length > 0;
    const noDevice = devList.cbs.length === 0 && devList.dws.length === 0;
    const cbsGrid = `236px 100px repeat(${labels.length}, 92px)`;
    const dwsGrid = `1fr repeat(${dwsCols.length}, 110px)`;
    const nameCell = (
        label: string,
        level: number,
        hasKids: boolean,
        isOpen: boolean,
        onToggle: () => void,
        info?: string
    ) => (
        <div
            onClick={hasKids ? onToggle : undefined}
            className="flex items-center"
            style={{
                gap: 5,
                paddingLeft: level * 15,
                cursor: hasKids ? 'pointer' : 'default',
                color: level > 1 ? C.text2 : C.text1,
                userSelect: 'none'
            }}
        >
            <span style={{ width: 10, color: C.text3, fontSize: 9 }}>{hasKids ? (isOpen ? '▾' : '▸') : ''}</span>
            <span>{label}</span>
            <Info text={info} />
        </div>
    );
    const rowInfo = (group: 'cbsRows' | 'dwsRows', row: Metric<unknown>) =>
        row.info ? k(`${group}.${row.key}.info`, { cap: fmt.num(DESIGN_CAPACITY) }) : undefined;

    return (
        <div className="mt-3">
            <div className="mb-1 flex flex-wrap items-baseline gap-2">
                <div className="text-[14px] font-semibold" style={{ color: C.text1 }}>
                    {k('title', { cbs: devList.cbs.length, dws: devList.dws.length })}
                </div>
                <span className="text-[12.5px]" style={{ color: C.text3 }}>
                    {k('range', {
                        a: fmt.hourText(labels[0] ?? ''),
                        b: fmt.hourText(labels[labels.length - 1] ?? ''),
                        n: labels.length
                    })}
                </span>
                {(noDevice || !hasData) && (
                    <span className="rounded px-2 py-0.5 text-[12px]" style={{ background: C.headBg, color: C.text3 }}>
                        {noDevice ? k('noDevice') : k('noData')}
                    </span>
                )}
            </div>

            {cbsData.map((blk) => (
                <div key={blk.key}>
                    <div className="mb-1 mt-3 text-[12.5px] font-semibold" style={{ color: C.text1 }}>
                        {k('cbs')}
                        {blk.kind === 'overall' && ` · ${k('overallN', { n: devList.cbs.length })}`}
                        {blk.kind === 'device' && ` · ${blk.dev?.name ?? ''}`}
                        <span className="ml-2 font-normal" style={{ color: C.text3 }}>
                            {k('cbsHint')}
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <div style={{ minWidth: 336 + labels.length * 92 }}>
                            <div
                                className="grid text-[12px]"
                                style={{
                                    gridTemplateColumns: cbsGrid,
                                    color: C.text2,
                                    background: C.headBg,
                                    borderBottom: `1px solid ${C.border}`,
                                    padding: '7px 6px'
                                }}
                            >
                                <div>{k('colMetric')}</div>
                                <div style={{ fontWeight: 600, color: C.text1 }}>{k('colTotal')}</div>
                                {labels.map((l) => (
                                    <div key={l}>{fmt.hourText(l)}</div>
                                ))}
                            </div>
                            {visC.map(({ row, hasKids }) => (
                                <div
                                    key={row.key}
                                    className="grid text-[12px]"
                                    style={{
                                        gridTemplateColumns: cbsGrid,
                                        color: C.text1,
                                        borderBottom: `1px solid ${C.borderLight}`,
                                        padding: '5px 6px',
                                        fontWeight: row.level === 0 ? 600 : 400
                                    }}
                                >
                                    {nameCell(
                                        k(`cbsRows.${row.key}.label`),
                                        row.level,
                                        hasKids,
                                        openC.has(row.key),
                                        () => toggle(openC, setOpenC, row.key),
                                        rowInfo('cbsRows', row as Metric<unknown>)
                                    )}
                                    <div style={{ fontWeight: 600, color: row.get && row.hot?.(blk.total) ? '#F53F3F' : undefined }}>
                                        {row.get ? row.get(blk.total, f) : '—'}
                                    </div>
                                    {labels.map((l) => {
                                        const b = blk.byHour.get(l) ?? emptyBucket();
                                        return (
                                            <div key={l} style={{ color: row.get && row.hot?.(b) ? '#F53F3F' : undefined }}>
                                                {row.get ? row.get(b, f) : '—'}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}

            <div className="mb-1 mt-4 text-[12.5px] font-semibold" style={{ color: C.text1 }}>
                DWS
                <span className="ml-2 font-normal" style={{ color: C.text3 }}>
                    {devList.dws.length > 1 ? k('dwsHintOverall') : k('dwsHint')}
                </span>
            </div>
            <div className="overflow-x-auto">
                <div style={{ minWidth: 300 + dwsCols.length * 110 }}>
                    <div
                        className="grid text-[12px]"
                        style={{
                            gridTemplateColumns: dwsGrid,
                            color: C.text2,
                            background: C.headBg,
                            borderBottom: `1px solid ${C.border}`,
                            padding: '7px 6px'
                        }}
                    >
                        <div>{k('colMetric')}</div>
                        {dwsCols.map((c) => (
                            <div key={c.key} className="text-right" style={c.overall ? { fontWeight: 600, color: C.text1 } : undefined}>
                                {c.overall ? k('overall') : c.name}
                            </div>
                        ))}
                    </div>
                    {visD.map(({ row, hasKids }) => (
                        <div
                            key={row.key}
                            className="grid text-[12px]"
                            style={{
                                gridTemplateColumns: dwsGrid,
                                color: C.text1,
                                borderBottom: `1px solid ${C.borderLight}`,
                                padding: '5px 6px',
                                fontWeight: row.level === 0 ? 600 : 400
                            }}
                        >
                            {nameCell(
                                k(`dwsRows.${row.key}.label`),
                                row.level,
                                hasKids,
                                openD.has(row.key),
                                () => toggle(openD, setOpenD, row.key),
                                rowInfo('dwsRows', row as Metric<unknown>)
                            )}
                            {dwsCols.map((c) => (
                                <div key={c.key} className="text-right" style={c.overall ? { fontWeight: 600 } : undefined}>
                                    {row.get ? row.get(c.b, f) : '—'}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
            <div className="mt-2 text-[12px] leading-relaxed" style={{ color: C.text3 }}>
                {k('footer')}
            </div>
        </div>
    );
};

export default SorterPanel;
